/* Iter274/280 — Coaching Setup Modal + Scheduled Coaching Panel.

   The modal opens automatically when an OPS admin flips a court to
   `reserved_coaching` (from the status select) OR presses the Edit
   button on the Scheduled Coaching panel. The panel lives at the top
   of the OPS view and lists every upcoming coaching session with a
   live countdown that updates every second.

   Iter280 — Multi-court coaching. A single session can span multiple
   courts simultaneously (`coaching_group_id`). The modal now has a
   chip picker for court selection and posts to the group-aware
   `PUT /festival/{id}/coaching/sessions` endpoint. The panel groups
   linked rows into one session line showing every court in the group.
*/
import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";

// Iter342 · New bug — `toast.error(e?.response?.data?.detail || ...)`
// crashed React when the backend detail was a structured object
// (e.g. the 409 `coaching_flip_conflict` payload with keys
// {message, code, released_player_ids, seated_by_court, court_state}).
// Sonner / React tried to render the object as a child. Extract a
// string across every path.
function formatApiError(err, fallback) {
  const d = err?.response?.data?.detail;
  if (typeof d === "string") return d;
  if (d && typeof d === "object") return d.message || fallback;
  return err?.message || fallback;
}


function fmtCountdown(sec) {
  if (sec <= 0) return "starting now";
  const h = Math.floor(sec / 3600);
  const m = Math.floor((sec % 3600) / 60);
  if (h > 0) return `in ${h}h ${m}m`;
  if (m > 0) return `in ${m}m`;
  return `in ${sec}s`;
}

// Iter275/280 — Derive the human-readable phase label for a scheduled
// coaching session. Drives the badge colour on the panel row.
// Iter359 · Bug 2 — Multi-slot support. Each row now carries slot
// fields via `court` (merged in ScheduledCoachingPanel from the
// queue). Phase is derived from the SLOT'S own time window, not the
// court's status. Only the slot whose (start .. start+duration)
// contains `now` shows as In Progress. Future queued slots stay in
// Setting Up / Approaching / Active regardless of the court's
// current status.
function coachingPhase(court, nowMs) {
  const slotMs = court.coaching_slot_at ? new Date(court.coaching_slot_at).getTime() : 0;
  if (!slotMs) return { key: "active", label: "Active", tone: "text-[#CCFF00]" };
  const durMs = (court.coaching_duration_minutes || 60) * 60000;
  const status = court.status || "active";
  const hasCurrent = (court.current_player_ids || []).length > 0;
  const isCurrentSlot = nowMs >= slotMs && nowMs <= slotMs + durMs;
  // In-progress only for THE currently-running slot AND when the
  // court status matches. Queued future slots on a court whose
  // status is already reserved_coaching (because an earlier slot is
  // live) stay in the future-tense labels below.
  if (isCurrentSlot && status === "reserved_coaching" && hasCurrent) {
    return { key: "in_progress", label: "In Progress", tone: "text-[#66C2FF]" };
  }
  if (isCurrentSlot && status === "reserved_coaching") {
    return { key: "setting_up", label: "Setting Up", tone: "text-[#FFB020]" };
  }
  const minutesOut = (slotMs - nowMs) / 60000;
  if (minutesOut <= 30 && minutesOut > 0) return { key: "approaching", label: "Approaching", tone: "text-[#FF4500]" };
  return { key: "active", label: "Active", tone: "text-[#CCFF00]" };
}

function toLocalDatetimeInput(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    const pad = (n) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  } catch { return ""; }
}

export function CoachingSetupModal({ court, allCourts, api, festivalId, onClose, onSaved }) {
  // Iter280/364 — Each coaching session is tied to exactly ONE court
  // (multi-court group support removed in Iter364). The picker still
  // renders every court but only one may be selected at a time; the
  // clicked court replaces the previous selection instead of adding
  // to it.
  const groupId = null;
  const isEdit = !!court?.coaching_slot_at;
  const [selectedIds, setSelectedIds] = useState(court?.id ? [court.id] : []);
  // Iter281 — Fresh form for every new session. Only pre-fill datetime
  // fields when editing an existing group. `key` on parent guarantees
  // remount per open so useState initial values are recomputed too.
  const [name, setName] = useState(isEdit ? (court?.coaching_session_name || "") : "");
  const [coach, setCoach] = useState(isEdit ? (court?.coaching_coach_name || "") : "");
  const [level, setLevel] = useState(isEdit ? (court?.coaching_level || "u35") : "u35");
  const [startAt, setStartAt] = useState(isEdit ? toLocalDatetimeInput(court?.coaching_slot_at) : "");
  const [maxPlayers, setMaxPlayers] = useState(isEdit ? (court?.coaching_max_players || 6) : 6);
  const [duration, setDuration] = useState(isEdit ? (court?.coaching_duration_minutes || 60) : 60);
  const [openNow, setOpenNow] = useState(isEdit ? !!court?.coaching_registration_forced_open_at : false);
  const [opensAt, setOpensAt] = useState(isEdit ? toLocalDatetimeInput(court?.coaching_registration_opens_at) : "");
  const [saving, setSaving] = useState(false);

  const pickableCourts = useMemo(() => {
    // Iter287 — Show EVERY court that exists in this festival. No
    // hidden filtering — admin sees the full picker and the chip
    // states (selectable / red-conflict / current-group) come from
    // the per-chip render logic below.
    return allCourts || [];
  }, [allCourts]);

  // Iter287 — Compute a per-court time conflict given the current
  // start/duration input. Returns null when no conflict OR admin
  // hasn't picked a start yet, otherwise `{start, end, freeAt}` with
  // pre-formatted HH:MM strings for the inline chip message.
  const courtConflict = (c) => {
    if (!startAt) return null;
    if (!c || !c.coaching_slot_at) return null;
    if (isEdit && c.coaching_group_id === groupId) return null;
    const newStart = new Date(startAt).getTime();
    const dur = Math.max(45, Math.min(120, Number(duration) || 60));
    const newEnd = newStart + dur * 60000;
    const otherStart = new Date(c.coaching_slot_at).getTime();
    const otherEnd = otherStart + (c.coaching_duration_minutes || 60) * 60000;
    if (otherEnd <= Date.now()) return null;
    if (!(newStart < otherEnd && otherStart < newEnd)) return null;
    const fmt = (ms) => new Date(ms).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
    return { start: fmt(otherStart), end: fmt(otherEnd), freeAt: fmt(otherEnd) };
  };

  const availableCourts = useMemo(() => {
    if (!startAt) return [];
    return pickableCourts.filter((c) => !courtConflict(c));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pickableCourts, startAt, duration, isEdit, groupId]);

  const toggleCourt = (cid) => {
    // Iter364 — Single-court sessions only. Clicking a court REPLACES
    // the selection; clicking the already-selected court clears it.
    setSelectedIds((prev) => (prev.includes(cid) ? [] : [cid]));
  };

  const save = async (confirmRelease = false, allowPlayToFinish = false) => {
    if (!selectedIds.length) { toast.error("Pick at least one court"); return; }
    if (!name.trim()) { toast.error("Session name is required"); return; }
    if (!coach.trim()) { toast.error("Coach name is required"); return; }
    if (!startAt) { toast.error("Start time is required"); return; }
    const newStart = new Date(startAt).getTime();
    const dur = Math.max(45, Math.min(120, Number(duration) || 60)) * 60000;
    const newEnd = newStart + dur;
    const feConflicts = [];
    const nowMs = Date.now();
    for (const c of allCourts || []) {
      if (!selectedIds.includes(c.id)) continue;
      if (!c.coaching_slot_at) continue;
      if (isEdit && c.coaching_group_id === groupId) continue;
      const otherStart = new Date(c.coaching_slot_at).getTime();
      const otherDur = (c.coaching_duration_minutes || 60) * 60000;
      const otherEnd = otherStart + otherDur;
      if (otherEnd <= nowMs) continue;
      if (newStart < otherEnd && otherStart < newEnd) {
        const s = new Date(otherStart).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
        const e = new Date(otherEnd).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
        feConflicts.push(`${c.name} already has a session scheduled from ${s} to ${e}`);
      }
    }
    if (feConflicts.length) {
      toast.error(`Time conflict — ${feConflicts.join("; ")}`);
      return;
    }
    setSaving(true);
    setSaveError("");
    try {
      // Iter418/420/421 · Fix — Resolve the editing session's gid
      // defensively. Priority order:
      //   1. `court.coaching_group_id` (top-level mirror).
      //   2. Match the session in the target court's
      //      `coaching_slot_queue` by (`coaching_session_name` AND
      //      `coaching_slot_at`) — use that queue entry's gid.
      //   3. Iter421 · Widen the search to EVERY court in
      //      `allCourts`: match by (name AND slot_at) against both
      //      each court's queue AND its top-level mirror. Some
      //      Live Ops edit paths pass a court doc whose own queue
      //      no longer carries the row directly.
      // If still null after every attempt, emit a `console.warn` so
      // the null send to the backend is never silent.
      let resolvedGid = null;
      if (isEdit) {
        resolvedGid = court?.coaching_group_id || null;
        const wantName = (court?.coaching_session_name || "").trim();
        const wantAtMs = court?.coaching_slot_at
          ? new Date(court.coaching_slot_at).getTime()
          : null;
        const nameSlotMatch = (a, b) => {
          if (!a || !b) return false;
          const aName = (a.coaching_session_name || "").trim();
          const bName = (b.coaching_session_name || "").trim();
          const aMs = a.coaching_slot_at ? new Date(a.coaching_slot_at).getTime() : null;
          const bMs = b.coaching_slot_at ? new Date(b.coaching_slot_at).getTime() : null;
          return !!aName && aName === bName && !!aMs && aMs === bMs;
        };
        const target = { coaching_session_name: wantName, coaching_slot_at: court?.coaching_slot_at };
        // Step 2 — same-court queue match.
        if (!resolvedGid && Array.isArray(court?.coaching_slot_queue)) {
          const m = court.coaching_slot_queue.find((q) => nameSlotMatch(q, target));
          resolvedGid = m?.coaching_group_id || null;
        }
        // Step 3 — scan every court in the festival.
        if (!resolvedGid) {
          for (const cc of (allCourts || [])) {
            if (Array.isArray(cc?.coaching_slot_queue)) {
              const m = cc.coaching_slot_queue.find((q) => nameSlotMatch(q, target));
              if (m?.coaching_group_id) { resolvedGid = m.coaching_group_id; break; }
            }
            if (nameSlotMatch(cc, target) && cc.coaching_group_id) {
              resolvedGid = cc.coaching_group_id;
              break;
            }
          }
        }
        if (!resolvedGid) {
          // eslint-disable-next-line no-console
          console.warn(
            "[iter421] coaching edit PUT — could not resolve coaching_group_id after " +
            "top-level + same-court queue + all-court scan; backend will receive null",
            {
              courtId: court?.id,
              courtGid: court?.coaching_group_id,
              sessionName: wantName,
              slotAt: court?.coaching_slot_at,
              queueLen: Array.isArray(court?.coaching_slot_queue) ? court.coaching_slot_queue.length : 0,
              queue: court?.coaching_slot_queue,
              allCourtsCount: (allCourts || []).length,
            }
          );
        } else {
          // eslint-disable-next-line no-console
          console.info(
            "[iter421] coaching edit PUT",
            {
              resolvedGid,
              via: court?.coaching_group_id === resolvedGid ? "top-level" : "queue-or-allcourt-match",
              courtGid: court?.coaching_group_id,
            }
          );
        }
      }
      const body = {
        court_ids: selectedIds,
        // Iter366 · Bug 6 / Iter418 · Fix — On EDIT we send the
        // resolved gid so the backend overlap check skips the entry
        // being replaced (previously false-positive'd against
        // itself). Iter364 removed multi-court but a new/edit gid is
        // still required for the queue.
        coaching_group_id: isEdit ? resolvedGid : null,
        coaching_session_name: name.trim(),
        coaching_coach_name: coach.trim(),
        coaching_level: level,
        coaching_slot_at: new Date(startAt).toISOString(),
        coaching_max_players: Math.max(4, Math.min(12, Number(maxPlayers) || 6)),
        coaching_duration_minutes: Math.max(45, Math.min(120, Number(duration) || 60)),
        coaching_registration_opens_at: (!openNow && opensAt) ? new Date(opensAt).toISOString() : null,
        force_open_now: !!openNow,
        // Iter332 · Bug 15 (real-flow) — flag propagates when the
        // organiser confirms the "release seated players" prompt.
        confirm_release: !!confirmRelease,
        // Iter424 · Restore "Allow Play to Finish" — third choice on
        // the 409 dialog. Backend skips the 409 seated-players guard,
        // never wipes current seats, clears only Next-Up on target
        // courts, and flags `coaching_pending_decision=true` so the
        // Iter275 T-10 sweep waits for the running game to end.
        allow_play_to_finish: !!allowPlayToFinish,
      };
      await api.put(`/festival/${festivalId}/coaching/sessions`, body);
      toast.success(`Coaching session scheduled on ${selectedIds.length} court`);
      onSaved && onSaved();
      onClose && onClose();
    } catch (e) {
      // Iter332 · Bug 15 — Catch the backend's 409 conflict envelope
      // and open the release-confirmation dialog listing displaced
      // players. Organiser can confirm → Save re-runs with
      // confirm_release=true, or cancel to pick different courts.
      const detail = e?.response?.data?.detail;
      if (e?.response?.status === 409 && detail && typeof detail === "object" && detail.code === "coaching_flip_conflict") {
        setReleaseConflict({
          seated_by_court: detail.seated_by_court || {},
          released_player_ids: detail.released_player_ids || [],
          message: detail.message || "Selected courts have seated players.",
        });
      } else {
        // Iter412 · Fix — Surface the exact backend detail inline so
        // OPS understands *why* the edit was blocked (5-min cutoff,
        // overlapping session, etc.) rather than a silent no-op.
        const msg = formatApiError(e, "Couldn't save session");
        setSaveError(msg);
        toast.error(msg);
      }
    } finally { setSaving(false); }
  };
  // Iter332 · Bug 15 (real-flow) — Release-confirmation dialog state.
  const [releaseConflict, setReleaseConflict] = useState(null);
  // Iter412 · Fix — Inline error surface for save failures. Displays
  // above the action row so OPS sees exactly why the edit was
  // rejected (typically the 5-min window backend guard or an
  // overlapping session), instead of just a fleeting toast.
  const [saveError, setSaveError] = useState("");

  return (
    <div className="fixed inset-0 z-[80] bg-black/70 flex items-start justify-center p-4 overflow-y-auto" onClick={onClose} data-testid="ops-coaching-modal">
      <div className="w-full max-w-lg bg-[#0A0A0A] border border-[#CCFF00] mt-12 p-5 space-y-3" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <div className="text-[#CCFF00] uppercase tracking-widest font-bold text-sm">
            Coaching Session {groupId ? "· Edit" : "· New"}
          </div>
          <button type="button" onClick={onClose} className="text-[#A3A3A3] hover:text-white text-lg leading-none">×</button>
        </div>

        {/* Iter280/287/364 — Single-court picker. Multi-select was
             removed in Iter364; the picker replaces the selection on
             every click. Every court is still visible; chip colour
             reflects conflict state for the chosen slot. */}
        <div>
          <div className="text-[10px] uppercase tracking-wider text-[#A3A3A3] mb-1">Which court will host this session?</div>
          <div className="flex flex-wrap gap-1" data-testid="ops-coach-modal-courts">
            {pickableCourts.map((c) => {
              const on = selectedIds.includes(c.id);
              const conflict = courtConflict(c);
              const chipBase = "px-2 py-1 text-[10px] uppercase tracking-wider border";
              const chipCls = conflict && !on
                ? "border-[#FF4500] text-[#FF4500] hover:bg-[#FF4500] hover:text-black"
                : on
                  ? (conflict
                    ? "bg-[#FF4500] text-black border-[#FF4500]"
                    : "bg-[#CCFF00] text-black border-[#CCFF00]")
                  : "border-[#525252] text-[#A3A3A3] hover:border-[#CCFF00] hover:text-[#CCFF00]";
              return (
                <button
                  key={c.id}
                  type="button"
                  onClick={() => toggleCourt(c.id)}
                  data-testid={`ops-coach-modal-court-${c.id}`}
                  className={`${chipBase} ${chipCls}`}
                >
                  {c.name}
                  {conflict && <span className="ml-1 text-[9px] font-bold">⚠</span>}
                </button>
              );
            })}
          </div>
          {/* Iter287 — Inline conflict messages for every conflicting
               court that is currently in the picker. */}
          {startAt && pickableCourts.some((c) => courtConflict(c)) && (
            <div className="mt-2 space-y-1" data-testid="ops-coach-conflicts">
              {pickableCourts.filter((c) => courtConflict(c)).map((c) => {
                const cf = courtConflict(c);
                return (
                  <div key={c.id} data-testid={`ops-coach-conflict-${c.id}`}
                    className="text-[10px] text-[#FF4500] border border-[#FF4500]/40 bg-[#4B0F0F]/40 px-2 py-1">
                    <span className="font-bold">{c.name}:</span> This court already has a session from {cf.start} to {cf.end}. It will be free from {cf.freeAt}.
                  </div>
                );
              })}
            </div>
          )}
          {/* Iter287 — Available courts hint list. */}
          {startAt && (
            <div className="mt-2 text-[10px]" data-testid="ops-coach-available-list">
              <span className="text-[#66C2FF] uppercase tracking-wider mr-1">Available courts for this time:</span>
              {availableCourts.length === 0 ? (
                <span className="text-[#FFB020]">None — change the start time or duration.</span>
              ) : (
                <span className="text-[#CCFF00]">{availableCourts.map((c) => c.name).join(", ")}</span>
              )}
            </div>
          )}
          {selectedIds.length > 1 && (
            <div className="text-[9px] text-[#FF4500] mt-1">Only one court per session is supported.</div>
          )}
        </div>

        <label className="block">
          <div className="text-[10px] uppercase tracking-wider text-[#A3A3A3] mb-1">What is this coaching session called?</div>
          <input type="text" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Volley Speed Up" maxLength={120}
            data-testid="ops-coach-modal-name" className="w-full bg-transparent border border-[#262626] text-white px-2 py-2 focus:outline-none focus:border-[#CCFF00]" />
        </label>
        <label className="block">
          <div className="text-[10px] uppercase tracking-wider text-[#A3A3A3] mb-1">Who is running this session?</div>
          <input type="text" value={coach} onChange={(e) => setCoach(e.target.value)} placeholder="Coach name" maxLength={80}
            data-testid="ops-coach-modal-coach" className="w-full bg-transparent border border-[#262626] text-white px-2 py-2 focus:outline-none focus:border-[#CCFF00]" />
        </label>
        <div>
          <div className="text-[10px] uppercase tracking-wider text-[#A3A3A3] mb-1">Which level is this session for?</div>
          <div className="flex gap-2">
            {[["u35", "Under 3.5"], ["o35", "Over 3.5"]].map(([v, lbl]) => (
              <button key={v} type="button" onClick={() => setLevel(v)}
                data-testid={`ops-coach-modal-level-${v}`}
                className={`flex-1 py-2 text-xs uppercase tracking-wider border ${level === v ? "bg-[#CCFF00] text-black border-[#CCFF00]" : "border-[#262626] text-[#A3A3A3] hover:border-[#CCFF00]"}`}
              >{lbl}</button>
            ))}
          </div>
        </div>
        <label className="block">
          <div className="text-[10px] uppercase tracking-wider text-[#A3A3A3] mb-1">When does this session start?</div>
          <input type="datetime-local" value={startAt} onChange={(e) => setStartAt(e.target.value)}
            data-testid="ops-coach-modal-start" className="w-full bg-transparent border border-[#262626] text-white px-2 py-2 focus:outline-none focus:border-[#CCFF00]" />
        </label>
        <label className="block">
          <div className="text-[10px] uppercase tracking-wider text-[#A3A3A3] mb-1">How many players can attend per court? (default 6)</div>
          <input type="number" min={4} max={12} value={maxPlayers} onChange={(e) => setMaxPlayers(e.target.value)}
            data-testid="ops-coach-modal-max" className="w-full bg-transparent border border-[#262626] text-white px-2 py-2 focus:outline-none focus:border-[#CCFF00]" />
        </label>
        <label className="block">
          <div className="text-[10px] uppercase tracking-wider text-[#A3A3A3] mb-1">How long is this coaching session? (45 minutes to 2 hours)</div>
          <input type="number" min={45} max={120} step={5} value={duration} onChange={(e) => setDuration(e.target.value)}
            data-testid="ops-coach-modal-duration" className="w-full bg-transparent border border-[#262626] text-white px-2 py-2 focus:outline-none focus:border-[#CCFF00]" />
        </label>
        <div>
          <div className="text-[10px] uppercase tracking-wider text-[#A3A3A3] mb-1">When should players be able to sign up?</div>
          <label className="flex items-center gap-2 text-xs text-white mb-2">
            <input type="checkbox" checked={openNow} onChange={(e) => setOpenNow(e.target.checked)} data-testid="ops-coach-modal-open-now" />
            Open registration immediately
          </label>
          {!openNow && (
            <input type="datetime-local" value={opensAt} onChange={(e) => setOpensAt(e.target.value)}
              data-testid="ops-coach-modal-opens-at" className="w-full bg-transparent border border-[#262626] text-white px-2 py-2 focus:outline-none focus:border-[#CCFF00]" />
          )}
        </div>
        <div className="flex gap-2 pt-2">
          <button type="button" onClick={onClose} className="flex-1 py-2 text-xs uppercase tracking-wider border border-[#525252] text-[#A3A3A3] hover:border-white hover:text-white">Cancel</button>
          <button type="button" disabled={saving} onClick={() => save(false)}
            data-testid="ops-coach-modal-save"
            className="flex-1 py-2 text-xs uppercase tracking-wider bg-[#CCFF00] text-black font-bold disabled:bg-[#262626] disabled:text-[#525252]">
            {saving ? "Saving…" : "Save session"}
          </button>
        </div>
        {/* Iter412 · Fix — Inline save error. Persists until the next
             save attempt so OPS can read the full message (toast
             disappears in 3 s). Typical trigger: backend rejected the
             edit because the session is inside the 5-min cutoff or
             overlaps another session on the selected court. */}
        {saveError && (
          <div
            data-testid="ops-coach-modal-error"
            role="alert"
            className="mt-2 border border-[#FF4500] bg-[#4B0F0F] text-[#FF6B4B] px-3 py-2 text-[11px] leading-snug"
          >
            <div className="uppercase tracking-wider text-[10px] font-bold mb-1">Edit blocked</div>
            <div className="text-white">{saveError}</div>
          </div>
        )}
        {/* Iter332 · Bug 15 (real-flow) — Release-confirmation dialog.
            Fires when the backend returns a 409 coaching_flip_conflict. */}
        {releaseConflict && (
          <div className="fixed inset-0 z-[95] bg-black/80 flex items-center justify-center p-4" data-testid="ops-coach-release-modal" onClick={() => setReleaseConflict(null)}>
            <div className="w-full max-w-md bg-[#0A0A0A] border-2 border-[#FF4500] p-6 shadow-2xl" onClick={(e) => e.stopPropagation()}>
              <div className="label-mono text-[#FF4500] mb-2">RELEASE SEATED PLAYERS?</div>
              <div className="text-sm text-white mb-3">{releaseConflict.message}</div>
              <div className="text-xs text-[#A3A3A3] mb-4">
                {releaseConflict.released_player_ids.length} player{releaseConflict.released_player_ids.length !== 1 ? "s" : ""} will be returned to the Available pool when the courts reserve for coaching. This can't be undone with a click.
                <div className="mt-2 text-[#CCFF00]">Or choose <span className="font-bold">Allow play to finish</span> to let the current game complete first — only Next-Up will be cleared now.</div>
              </div>
              <div className="flex justify-end gap-2 flex-wrap">
                <button type="button" data-testid="ops-coach-release-cancel" onClick={() => setReleaseConflict(null)} className="px-3 py-1.5 text-xs border border-[#525252] text-[#A3A3A3] hover:border-white hover:text-white">Cancel</button>
                {/* Iter424 · Restore "Allow Play to Finish" — third
                     choice on the 409 dialog. Lets the current game
                     finish naturally; only Next-Up is cleared now.
                     Backend flags `coaching_pending_decision=true` on
                     the affected courts so the Iter275 T-10 sweep
                     preserves current seats and reserves the court on
                     natural game end. */}
                <button type="button" data-testid="ops-coach-release-allow-finish" onClick={() => { setReleaseConflict(null); save(false, true); }} className="px-3 py-1.5 text-xs border border-[#CCFF00] text-[#CCFF00] hover:bg-[#CCFF00] hover:text-black">Allow play to finish</button>
                <button type="button" data-testid="ops-coach-release-confirm" onClick={() => { setReleaseConflict(null); save(true); }} className="px-3 py-1.5 text-xs bg-[#FF4500] text-white hover:bg-[#E63E00]">Release & schedule</button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export function ScheduledCoachingPanel({ courts, players, api, festivalId, onEdit, onCreate, onChanged }) {
  const [nowTick, setNowTick] = useState(Date.now());
  useEffect(() => { const t = setInterval(() => setNowTick(Date.now()), 1000); return () => clearInterval(t); }, []);
  // Iter360 · Bug 1 — Auto-dismiss the blue "released back to active"
  // release note after 5s, and ensure it never duplicates across
  // session rows. We track gids we've already auto-dismissed so a
  // stale poll response doesn't fire the dismiss endpoint twice.
  const autoDismissedGids = useRef(new Set());
  // Iter362 · Bug 4 — Optimistic-hide set for gids the OPS user has
  // just clicked DISMISS on. Keeps the banner out of the DOM until
  // the next poll actually returns without the note.
  const [manuallyDismissedGids, setManuallyDismissedGids] = useState(() => new Set());
  // Iter351 · Fix 4 — Look up signup names for the hover tooltip.
  const playerById = useMemo(
    () => Object.fromEntries((players || []).map((p) => [p.id, p])),
    [players],
  );
  // Iter277 — Cancel All Coaching — wipes every scheduled session on
  // the festival and returns every affected court to Active.
  const cancelAll = async () => {
    if (!window.confirm("Cancel every scheduled coaching session? All affected courts return to Active immediately.")) return;
    try {
      const r = await api.post(`/festival/${festivalId}/coaching/cancel-all`);
      toast.success(`Cancelled ${r?.data?.affected || 0} coaching session(s)`);
      onChanged && onChanged();
    } catch (e) { toast.error(formatApiError(e, "Couldn't cancel")); }
  };
  // Iter280 — Group courts by coaching_group_id so a single multi-court
  // session renders as ONE row that lists every court it spans.
  // Iter356 · Bug 1 — Each court doc now carries a `coaching_slot_queue`
  // of ALL scheduled sessions (present + future). Flatten every court's
  // queue (plus the legacy top-level slot if not already in the queue)
  // so the panel shows every scheduled session, not just the imminent
  // one. Grouping by coaching_group_id still consolidates multi-court
  // sessions into one row.
  const sessions = useMemo(() => {
    const slotEntries = [];
    for (const c of (courts || [])) {
      const queue = Array.isArray(c.coaching_slot_queue) ? c.coaching_slot_queue : [];
      const seenGids = new Set(queue.map((q) => q.coaching_group_id).filter(Boolean));
      const flat = [...queue];
      // Fall back to legacy top-level slot when the queue is empty
      // (old docs pre-Iter356) or when it doesn't yet include the
      // legacy imminent session.
      if (c.coaching_slot_at && (!c.coaching_group_id || !seenGids.has(c.coaching_group_id))) {
        flat.push({
          coaching_group_id: c.coaching_group_id,
          coaching_master_court_id: c.coaching_master_court_id,
          coaching_session_name: c.coaching_session_name,
          coaching_coach_name: c.coaching_coach_name,
          coaching_level: c.coaching_level,
          coaching_slot_at: c.coaching_slot_at,
          coaching_duration_minutes: c.coaching_duration_minutes,
          coaching_max_players: c.coaching_max_players,
          coaching_signup_ids: c.coaching_signup_ids || [],
          coaching_registration_opens_at: c.coaching_registration_opens_at,
          coaching_registration_forced_open_at: c.coaching_registration_forced_open_at,
        });
      }
      for (const slot of flat) {
        if (!slot.coaching_slot_at) continue;
        try {
          const startMs = new Date(slot.coaching_slot_at).getTime();
          const durMs = (slot.coaching_duration_minutes || 60) * 60000;
          if ((startMs + durMs) <= nowTick - 60000) continue;
        } catch { continue; }
        slotEntries.push({ ...slot, _court: c });
      }
    }
    const groups = new Map();
    for (const s of slotEntries) {
      const key = s.coaching_group_id || `solo::${s._court?.id}::${s.coaching_slot_at}`;
      if (!groups.has(key)) groups.set(key, { slot: s, courts: [] });
      // De-dup: only add each court once per group.
      const g = groups.get(key);
      if (!g.courts.some((cc) => cc.id === s._court?.id)) g.courts.push(s._court);
    }
    const rows = [];
    for (const [key, { slot, courts: linked }] of groups.entries()) {
      linked.sort((a, b) => (a.name || "").localeCompare(b.name || ""));
      // The "anchor" is a synthetic view: legacy fields at top-level
      // sourced from the SLOT (so signup counts / times reflect this
      // specific session, not the court's currently-imminent one).
      const anchor = {
        ...linked[0],
        coaching_group_id: slot.coaching_group_id,
        coaching_session_name: slot.coaching_session_name,
        coaching_coach_name: slot.coaching_coach_name,
        coaching_level: slot.coaching_level,
        coaching_slot_at: slot.coaching_slot_at,
        coaching_duration_minutes: slot.coaching_duration_minutes,
        coaching_max_players: slot.coaching_max_players,
        coaching_signup_ids: slot.coaching_signup_ids || [],
        // Provide per-court linked entries whose signup arrays are
        // pinned to THIS slot (from the queue), so header counts
        // reflect the correct session.
      };
      const linkedPinned = linked.map((cc) => {
        const own = (cc.coaching_slot_queue || []).find((q) => q.coaching_group_id === slot.coaching_group_id);
        return own
          ? { ...cc, coaching_signup_ids: own.coaching_signup_ids || [],
              coaching_max_players: own.coaching_max_players || cc.coaching_max_players,
              current_player_ids: (cc.coaching_group_id === slot.coaching_group_id ? cc.current_player_ids : []) || [] }
          : cc;
      });
      rows.push({ key, anchor, courts: linkedPinned });
    }
    rows.sort((a, b) => new Date(a.anchor.coaching_slot_at || 0) - new Date(b.anchor.coaching_slot_at || 0));
    return rows;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [courts, nowTick]);
  if (sessions.length === 0) {
    return (
      <section className="mb-3 border border-[#CCFF00] bg-black p-3" data-testid="ops-scheduled-coaching-panel">
        <div className="flex items-center justify-between gap-2">
          <div className="text-[10px] uppercase tracking-widest text-[#CCFF00] font-bold">Coaching Scheduler</div>
          <button
            type="button"
            data-testid="ops-coaching-new-session"
            onClick={() => onCreate && onCreate()}
            className="text-[10px] px-3 py-1 bg-[#CCFF00] text-black font-bold uppercase tracking-widest"
          >+ New Session</button>
        </div>
        <div className="mt-2 text-[11px] text-[#A3A3A3] italic" data-testid="ops-scheduled-coaching-empty">
          No coaching sessions scheduled. Tap <span className="text-[#CCFF00]">+ New Session</span> to schedule one on any court(s).
        </div>
      </section>
    );
  }
  // Iter411 · Fix — GLOBAL anyLive gate. As soon as ANY coaching
  // session on ANY court is `status === "reserved_coaching"` (which
  // is exactly what drives `phase.key === "in_progress"`), hide every
  // Clear Court Early button in the scheduler header. Finish
  // Coaching Early on the court card is the sole exit path once a
  // session is live so signups are preserved for reporting.
  const anyLive = (courts || []).some((cc) => cc.status === "reserved_coaching");
  return (
    <section className="mb-3 border border-[#CCFF00] bg-black p-2" data-testid="ops-scheduled-coaching-panel">
      {(() => { /* no-op; anyLive declared below */ })()}      <div className="flex items-center justify-between mb-1 gap-2">
        <div className="text-[10px] uppercase tracking-widest text-[#CCFF00] font-bold" data-testid="ops-scheduled-coaching-count">Coaching Scheduler · {sessions.length} session{sessions.length > 1 ? "s" : ""}</div>
        <div className="flex gap-1">
          <button
            type="button"
            data-testid="ops-coaching-new-session"
            onClick={() => onCreate && onCreate()}
            className="text-[9px] px-2 py-0.5 bg-[#CCFF00] text-black font-bold uppercase tracking-wider"
          >+ New Session</button>
          <button type="button" onClick={cancelAll} data-testid="ops-coaching-cancel-all" className="text-[9px] px-2 py-0.5 border border-[#FF4500] text-[#FF4500] uppercase tracking-wider hover:bg-[#FF4500] hover:text-black">
            Cancel All Coaching
          </button>
        </div>
      </div>
      <ul className="space-y-1 max-h-[60vh] overflow-y-auto" data-testid="ops-scheduled-coaching-list">
        {sessions.map(({ key, anchor: c, courts: linked }) => {
          const startMs = new Date(c.coaching_slot_at).getTime();
          const remaining = Math.round((startMs - nowTick) / 1000);
          const startLabel = new Date(startMs).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
          const levelLabel = c.coaching_level === "u35" ? "Under 3.5" : c.coaching_level === "o35" ? "Over 3.5" : "";
          const courtsLabel = c.name;
          const totalSignups = linked.reduce((sum, x) => {
            // Iter354 · Bug 3 — Header count must include drag-drop
            // players (in current_player_ids) alongside self-signups
            // (in coaching_signup_ids), matching the court panel + the
            // hover tooltip. Union deduped so a player who's both
            // signed up AND on court isn't counted twice.
            const union = new Set([
              ...(x.status === "reserved_coaching" ? (x.current_player_ids || []) : []),
              ...((x.coaching_signup_ids) || []),
            ]);
            return sum + union.size;
          }, 0);
          const totalCap = linked.reduce((sum, x) => sum + (x.coaching_max_players || 6), 0);
          const gid = c.coaching_group_id;
          // Iter360 · Bug 1 — Only surface the release note on the
          // session row it actually belongs to. The note is stamped
          // on the COURT doc but a single court can carry multiple
          // upcoming sessions in its queue, so the same note was
          // previously showing up on every session row for that court.
          // Match by `coaching_group_release_note_gid` (server-stamped
          // in Iter360). Fall back to the legacy behaviour only when
          // the gid field is absent (older docs).
          const releaseNoteEntry = linked
            .map((x) => ({
              note: x.coaching_group_release_note,
              noteGid: x.coaching_group_release_note_gid,
              at: x.coaching_group_release_note_at,
            }))
            .find((e) => e.note && (!e.noteGid || e.noteGid === gid));
          const releaseNote = releaseNoteEntry?.note || null;
          const releaseNoteAt = releaseNoteEntry?.at || null;
          // Iter362 · Bug 4 — Suppress once the OPS user has clicked
          // DISMISS, even if the server hasn't yet cleared the note.
          const suppressedByUser = gid ? manuallyDismissedGids.has(gid) : false;
          const dismissReleaseNote = async () => {
            if (!gid) return;
            // Iter362 · Bug 4 — Optimistic hide so the banner leaves
            // the DOM the instant DISMISS is clicked, without waiting
            // for the next 3-second poll.
            setManuallyDismissedGids((s) => {
              const n = new Set(s); n.add(gid); return n;
            });
            try {
              await api.post(`/festival/${festivalId}/coaching/sessions/${gid}/dismiss-release-note`);
              onChanged && onChanged();
            } catch {}
          };
          // Iter360 · Bug 1 — Auto-dismiss after 5s (from the server's
          // stamp time, not first-render), so a stale note that's
          // already older than 5s clears on the next poll instead of
          // waiting another full 5s. Dedup via ref so we only hit the
          // dismiss endpoint once per gid.
          if (releaseNote && releaseNoteAt && gid && !autoDismissedGids.current.has(gid)) {
            try {
              const ageMs = nowTick - new Date(releaseNoteAt).getTime();
              if (ageMs >= 5000) {
                autoDismissedGids.current.add(gid);
                dismissReleaseNote();
              }
            } catch {}
          }
          // If the note was cleared (server-side or by us), forget the
          // gid so a future release stamp can be auto-dismissed again.
          if (!releaseNote && gid) {
            autoDismissedGids.current.delete(gid);
            if (manuallyDismissedGids.has(gid)) {
              setManuallyDismissedGids((s) => {
                if (!s.has(gid)) return s;
                const n = new Set(s); n.delete(gid); return n;
              });
            }
          }
          const reduceCourts = null;  // Iter364 — multi-court removed
          const cancel = async () => {
            // Iter364 — Simple single-court cancel. Prompt-based
            // "court or group" choice was removed alongside multi-
            // court support. Routes through the gid-scoped backend
            // endpoint so both queue-only and live sessions are
            // handled uniformly and every player is released to
            // RESTING.
            if (!window.confirm(`Cancel the coaching session on ${c.name}? Court returns to Active.`)) return;
            try {
              if (gid) {
                await api.post(`/festival/${festivalId}/coaching/sessions/${gid}/cancel`);
              } else {
                // Legacy fall-back for very old rows without a gid.
                await api.patch(`/festival/${festivalId}/courts/${c.id}`, {
                  status: "active",
                  coaching_slot_at: null,
                  coaching_session_name: null,
                  coaching_coach_name: null,
                  coaching_registration_opens_at: null,
                  coaching_registration_forced_open_at: null,
                });
              }
              toast.success("Session cancelled — court is Active");
              onChanged && onChanged();
            } catch (e) { toast.error(formatApiError(e, "Couldn't cancel")); }
          };
          const decisionPending = linked.some((x) => x.coaching_pending_decision);
          const anyUrgent = linked.some((x) => x.coaching_alert_urgent);
          const allow = async () => {
            try {
              await Promise.all(linked.map((x) => api.patch(`/festival/${festivalId}/courts/${x.id}`, {
                coaching_pending_decision: false,
                coaching_alert_urgent: false,
              })));
              toast.success(`Allowed to finish — court${linked.length > 1 ? "s" : ""} will reserve on game end`);
              onChanged && onChanged();
            } catch (e) { toast.error(formatApiError(e, "Couldn't allow")); }
          };
          const clearEarly = async () => {
            // Iter350/363 · Bug 1 — Clear Court Early on a coaching
            // court only ends the CURRENT game early. Coaching
            // session, signups, and coaching_* metadata must be
            // preserved so the session can resume with fresh signups.
            // Iter362 wrongly routed multi-court through the cancel
            // endpoint (which DELETES the session); Iter363 reverts
            // to the Iter350 semantics for both single- and multi-
            // court cases: reset each linked court + clear its
            // pending-decision flag, session record stays.
            const msg = linked.length > 1
              ? `End the current game on ${linked.length} courts now? Seated players return to Resting; the coaching session and any remaining signups stay intact.`
              : `End the current game on ${c.name} now? Seated players return to Resting; the coaching session and any remaining signups stay intact.`;
            if (!window.confirm(msg)) return;
            try {
              for (const x of linked) {
                await api.post(`/festival/${festivalId}/courts/${x.id}/reset`, { mode: "both" });
                await api.patch(`/festival/${festivalId}/courts/${x.id}`, {
                  coaching_pending_decision: false,
                  coaching_alert_urgent: false,
                });
              }
              toast.success(
                `Current game ended on ${linked.length > 1 ? `${linked.length} courts` : c.name} — coaching session preserved`,
              );
              onChanged && onChanged();
            } catch (e) { toast.error(formatApiError(e, "Couldn't end game")); }
          };
          const phase = coachingPhase(c, nowTick);
          // Iter351 · Fix 4 — Hover tooltip lists every signup name so
          // OPS can eyeball the roster before the court is allocated.
          // Iter352 · Bug 2 — Include current_player_ids too so drag-
          // drop additions are visible in the tooltip alongside the
          // right-click / self-signup entries.
          const signupPids = [...new Set(linked.flatMap((x) => [
            ...(x.status === "reserved_coaching" ? (x.current_player_ids || []) : []),
            ...(x.coaching_signup_ids || []),
          ]))];
          const signupNames = signupPids
            .map((pid) => playerById[pid])
            .filter(Boolean)
            .map((p) => `${p.first_name} ${p.last_name}`.trim());
          const rosterTitle = signupNames.length
            ? `Signed up:\n${signupNames.join("\n")}`
            : "No players signed up yet";
          // Iter351/352 · Fixes 3 & 5 — Phase-aware label. LIVE for
          // in_progress; SETTING UP for setting_up; countdown parens
          // for approaching/upcoming.
          const remainingLabel = phase.key === "in_progress"
            ? "LIVE"
            : phase.key === "setting_up"
              ? "SETTING UP"
              : `(${fmtCountdown(remaining)})`;
          return (
            <li key={key} data-testid={`ops-scheduled-coach-${c.id}`} title={rosterTitle} className={`text-[11px] text-white ${anyUrgent ? "bg-[#4B0F0F]/40 px-1" : ""}`}>
              <div className="flex items-center justify-between gap-2">
                <div className="flex-1 min-w-0 truncate">
                  <span className={`inline-block text-[9px] uppercase tracking-wider mr-2 ${phase.tone}`}>{phase.label}</span>
                  <span className="font-bold">{courtsLabel}</span>
                  {c.coaching_session_name && <> — <span className="text-[#CCFF00]">{c.coaching_session_name}</span></>}
                  {c.coaching_coach_name && <> with <span className="text-white">Coach {c.coaching_coach_name}</span></>}
                  {levelLabel && <span className="text-[#A3A3A3]"> · {levelLabel}</span>}
                  <span className="text-[#A3A3A3]"> · {startLabel} </span>
                  <span
                    className={phase.key === "in_progress" ? "text-[#66C2FF] font-bold" : "text-[#FFB020]"}
                    data-testid={`ops-scheduled-remaining-${c.id}`}
                  >
                    {remainingLabel}
                  </span>
                  <span className="text-[#A3A3A3]" data-testid={`ops-scheduled-count-${c.id}`}> · {totalSignups}/{totalCap} signed up</span>
                </div>
                {decisionPending ? (
                  <>
                    <button type="button" onClick={allow} data-testid={`ops-scheduled-allow-${c.id}`} className="text-[9px] px-2 py-0.5 border border-[#CCFF00] text-[#CCFF00] uppercase tracking-wider">Allow</button>
                    {/* Iter423 · Per-row gate — Show Clear Court
                         Early only when THIS row's court is not
                         `reserved_coaching` AND this row's phase is
                         not `in_progress` AND the session starts
                         within 60 minutes. Global `anyLive` check
                         removed so one live coaching court no longer
                         hides the button on unrelated rows. */}
                    {c.status !== "reserved_coaching" && phase.key !== "in_progress" && remaining <= 60 * 60 && (
                      <button type="button" onClick={clearEarly} data-testid={`ops-scheduled-clear-early-${c.id}`} className="text-[9px] px-2 py-0.5 border border-[#FF4500] text-[#FF4500] uppercase tracking-wider">Clear Court Early</button>
                    )}
                  </>
                ) : (
                  <>
                    {/* Iter423 · Per-row gate — Same three per-row
                         conditions: THIS row's court status is not
                         `reserved_coaching` AND `phase.key !==
                         "in_progress"` AND `remaining <= 60 * 60`. */}
                    {c.status !== "reserved_coaching" && phase.key !== "in_progress" && remaining <= 60 * 60 && (
                      <button type="button" onClick={clearEarly} data-testid={`ops-scheduled-clear-early-${c.id}`} className="text-[9px] px-2 py-0.5 border border-[#FF4500] text-[#FF4500] uppercase tracking-wider">Clear Court Early</button>
                    )}
                    {/* Iter412 · Fix — Edit is available until the
                         5-minute cutoff. Prior gate hid it only on
                         `in_progress`, letting OPS click within the
                         final minutes only to have the backend
                         reject with a session conflict silently.
                         `remaining` is seconds-to-start; hide as
                         soon as we're inside the 5-min window (or
                         already in progress). */}
                    {remaining > 5 * 60 && phase.key !== "in_progress" && (
                      <button type="button" onClick={() => onEdit && onEdit(c)} data-testid={`ops-scheduled-edit-${c.id}`} className="text-[9px] px-2 py-0.5 border border-[#525252] text-[#A3A3A3] uppercase tracking-wider hover:border-[#CCFF00] hover:text-[#CCFF00]">Edit</button>
                    )}
                    <button type="button" onClick={cancel} data-testid={`ops-scheduled-cancel-${c.id}`} className="text-[9px] px-2 py-0.5 border border-[#FF4500] text-[#FF4500] uppercase tracking-wider">Cancel</button>
                  </>
                )}
              </div>
              {releaseNote && !suppressedByUser && (
                <div className="flex items-center justify-between gap-2 mt-1 pl-1 border-l-2 border-[#66C2FF] text-[10px] text-[#66C2FF]" data-testid={`ops-release-note-${c.id}`}>
                  <span className="flex-1 truncate">{releaseNote}</span>
                  <button type="button" onClick={dismissReleaseNote} data-testid={`ops-release-note-dismiss-${c.id}`} className="text-[9px] px-1.5 py-0.5 border border-[#66C2FF] text-[#66C2FF] uppercase tracking-wider">Dismiss</button>
                </div>
              )}
            </li>
          );
        })}
      </ul>
    </section>
  );
}
